import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Spinner, Dropdown, Button, Row, Col, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Input, Select, Empty } from "antd";

import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import SubtleBadge from "components/common/SubtleBadge";
import useGuestSessions from "hooks/userManagement/useGuestSessions";
import IconButton from "components/common/IconButton";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import Guard from "components/access-control/rbac/Guard";

const SESSION_STATUS_COLORS = {
  ACTIVE: "success",
  EXPIRED: "warning",
  REVOKED: "danger",
};

// Session status styles
const SESSION_STATUS_STYLES = {
  ACTIVE: { border: "#198754", text: "#198754", bg: "#e6f4ea" },
  EXPIRED: { border: "#ffc107", text: "#b08900", bg: "#fff8e1" },
  REVOKED: { border: "#dc3545", text: "#dc3545", bg: "#fdeaea" },
};

const SESSION_FILTER_OPTIONS = [
  { label: "🌍 All Sessions", value: "all" },
  { label: "🟢 Active", value: "active" },
  { label: "🟡 Expired", value: "expired" },
  { label: "🔴 Revoked", value: "revoked" },
];

const GuestSessions = () => {
  const navigate = useNavigate();

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState("all");

  // Delete/Wipe state
  const [loadingAction, setLoadingAction] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showWipeExpiredModal, setShowWipeExpiredModal] = useState(false);
  const [showWipeAllModal, setShowWipeAllModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);

  const {
    sessions,
    stats,
    loading,
    error,
    totalPages,
    totalElements,
    fetchSessions,
    fetchStats,
    revokeSession,
    deleteSession,
    wipeExpiredSessions,
    wipeAllSessions,
  } = useGuestSessions();

  // Reset page on filter change
  useEffect(() => setPage(0), [statusFilter]);

  // Fetch sessions on load
  useEffect(() => {
    fetchSessions(page, pageSize, statusFilter);
    fetchStats();
  }, [page, pageSize, statusFilter, fetchSessions, fetchStats]);

  // Refetch on window focus
  useEffect(() => {
    const handleFocus = () => {
      fetchSessions(page, pageSize, statusFilter);
      fetchStats();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [page, pageSize, statusFilter, fetchSessions, fetchStats]);

  // Action handlers
  const handleViewDetails = useCallback((session) => {
    setSelectedSession(session);
    setShowDetailsModal(true);
  }, []);

  const confirmDelete = (sessionId) => {
    setSessionToDelete(sessionId);
    setShowDeleteModal(true);
    setActionError(null);
  };

  const handleDelete = async () => {
    if (!sessionToDelete) return;
    setLoadingAction(true);
    setActionError(null);
    try {
      const result = await deleteSession(sessionToDelete);
      if (result.success) {
        toast.success("Session deleted successfully.");
        await fetchSessions(page, pageSize, statusFilter);
        await fetchStats();
      } else {
        throw new Error(result.error || "Failed to delete session");
      }
    } catch (err) {
      const apiMessage = err?.response?.data?.message || err?.message || "Failed to delete session.";
      setActionError(apiMessage);
      toast.error(apiMessage);
    } finally {
      setShowDeleteModal(false);
      setSessionToDelete(null);
      setLoadingAction(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSessionToDelete(null);
    setActionError(null);
  };

  const handleRevoke = async (sessionId) => {
    setLoadingAction(true);
    try {
      const result = await revokeSession(sessionId, "Revoked by admin");
      if (result.success) {
        toast.success("Session revoked successfully.");
        await fetchSessions(page, pageSize, statusFilter);
        await fetchStats();
      } else {
        throw new Error(result.error || "Failed to revoke session");
      }
    } catch (err) {
      toast.error(err.message || "Failed to revoke session");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleWipeExpired = async () => {
    setLoadingAction(true);
    try {
      const result = await wipeExpiredSessions();
      if (result.success) {
        toast.success(`Wiped ${result.data.deletedCount} expired sessions.`);
        await fetchSessions(page, pageSize, statusFilter);
        await fetchStats();
      } else {
        throw new Error(result.error || "Failed to wipe expired sessions");
      }
    } catch (err) {
      toast.error(err.message || "Failed to wipe expired sessions");
    } finally {
      setShowWipeExpiredModal(false);
      setLoadingAction(false);
    }
  };

  const handleWipeAll = async () => {
    setLoadingAction(true);
    try {
      // Pass 0 to delete ALL sessions (no date filter)
      const result = await wipeAllSessions(0);
      console.log("[handleWipeAll] Result:", result);
      if (result.success && result.data) {
        const deletedCount = result.data.deletedCount ?? 0;
        toast.success(`Wiped ${deletedCount} sessions.`);
        await fetchSessions(page, pageSize, statusFilter);
        await fetchStats();
      } else {
        console.error("[handleWipeAll] Failed:", result.error);
        toast.error(result.error || "Failed to wipe all sessions");
      }
    } catch (err) {
      console.error("[handleWipeAll] Error:", err);
      toast.error(err.message || "Failed to wipe all sessions");
    } finally {
      setShowWipeAllModal(false);
      setLoadingAction(false);
    }
  };

  // Stats
  const displayStats = useMemo(() => ({
    ACTIVE: stats.active || 0,
    EXPIRED: stats.expired || 0,
    REVOKED: stats.revoked || 0,
    TOTAL: stats.total || 0,
  }), [stats]);

  // Table columns
  const columns = useMemo(() => [
    {
      accessorKey: "sessionId",
      header: "Session ID",
      cell: ({ getValue }) => {
        const value = getValue() || "None";
        return (
          <div
            title={value}
            style={{
              maxWidth: 90,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 12,
            }}
          >
            {value}
          </div>
        );
      },
    },
    {
      accessorKey: "deviceId",
      header: "Device ID",
      cell: ({ getValue }) => {
        const value = getValue() || "None";
        return (
          <div
            title={value}
            style={{
              maxWidth: 100,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 11,
            }}
          >
            {value}
          </div>
        );
      },
    },
    { accessorKey: "platform", header: "Platform" },
    { accessorKey: "clientApp", header: "Client App" },
    {
      accessorKey: "requestCount",
      header: "Requests",
      cell: ({ getValue }) => getValue() || 0,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const status = getValue() || "UNKNOWN";
        const colorTheme = SESSION_STATUS_COLORS[status] || "secondary";

        return (
          <div className="text-center">
            <SubtleBadge
              bg={colorTheme}
              className="fw-bold"
              style={{
                borderRadius: "4px",
                padding: "2px 6px",
                fontSize: "0.7rem",
                textTransform: "uppercase"
              }}
            >
              {status}
            </SubtleBadge>
          </div>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center fw-bold" },
        cellProps: { className: "text-center" },
      },
    },
    {
      accessorKey: "createdAt",
      header: "Created",
      cell: ({ getValue }) => {
        const date = getValue();
        return date ? new Date(date).toLocaleDateString() : "N/A";
      },
    },
    {
      accessorKey: "expiresAt",
      header: "Expires",
      cell: ({ getValue }) => {
        const date = getValue();
        return date ? new Date(date).toLocaleDateString() : "N/A";
      },
    },
    {
      id: "actions", header: "Actions", cell: ({ row }) => {
        const session = row.original;
        return (
          <Dropdown className="action-dropdown p-0">
            <Dropdown.Toggle as="button" className="bg-transparent border-0 p-0 shadow-none text-secondary">⋯</Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => handleViewDetails(session)}>View Details</Dropdown.Item>
              {session.status === "ACTIVE" && (
                <Dropdown.Item onClick={() => handleRevoke(session.sessionId)}>Revoke</Dropdown.Item>
              )}
              <Guard privilege="DELETE_USERS">
                <Dropdown.Divider />
                <Dropdown.Item className="text-danger" onClick={() => confirmDelete(session.sessionId)}>Delete</Dropdown.Item>
              </Guard>
            </Dropdown.Menu>
          </Dropdown>
        );
      }
    }
  ], [handleViewDetails, handleRevoke]);

  const table = useAdvanceTable({ data: sessions || [], columns, sortable: true, pagination: true, perPage: pageSize });

  return (
    <>
      <WidgetSectionTitle
        className="mb-3 mt-3"
        title="Guest Sessions"
        icon="user"
        subtitle="Manage guest user sessions for unauthenticated access."
        transform="shrink-2"
      />

      {/* ================= STATS + ACTIONS ================= */}
      <div
        className="position-relative p-3 rounded-3 mb-4 d-flex align-items-center justify-content-between flex-wrap"
        style={{
          background: "#f7f5f5",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        }}
      >
        {/* Stats boxes */}
        <div className="d-flex flex-nowrap gap-2 overflow-auto">
          <div
            className="p-2 rounded-3 d-flex flex-column align-items-center justify-content-center"
            style={{
              minWidth: 70,
              maxWidth: 90,
              flex: "0 1 auto",
              backgroundColor: "#fff",
              borderLeft: "4px solid #6c757d",
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 600, color: "#6c757d", marginBottom: 4, textAlign: "center" }}>
              TOTAL
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#6c757d" }}>
              {displayStats.TOTAL}
            </div>
          </div>

          <div
            className="p-2 rounded-3 d-flex flex-column align-items-center justify-content-center"
            style={{
              minWidth: 70,
              maxWidth: 90,
              flex: "0 1 auto",
              backgroundColor: "#fff",
              borderLeft: "4px solid #198754",
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 600, color: "#198754", marginBottom: 4, textAlign: "center" }}>
              ACTIVE
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#198754" }}>
              {displayStats.ACTIVE}
            </div>
          </div>

          <div
            className="p-2 rounded-3 d-flex flex-column align-items-center justify-content-center"
            style={{
              minWidth: 70,
              maxWidth: 90,
              flex: "0 1 auto",
              backgroundColor: "#fff",
              borderLeft: "4px solid #ffc107",
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 600, color: "#b08900", marginBottom: 4, textAlign: "center" }}>
              EXPIRED
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#b08900" }}>
              {displayStats.EXPIRED}
            </div>
          </div>

          <div
            className="p-2 rounded-3 d-flex flex-column align-items-center justify-content-center"
            style={{
              minWidth: 70,
              maxWidth: 90,
              flex: "0 1 auto",
              backgroundColor: "#fff",
              borderLeft: "4px solid #dc3545",
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 600, color: "#dc3545", marginBottom: 4, textAlign: "center" }}>
              REVOKED
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#dc3545" }}>
              {displayStats.REVOKED}
            </div>
          </div>
        </div>

        {/* Action buttons on right side */}
        <div className="ms-auto mt-2 mt-sm-0 d-flex gap-2">
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => setShowWipeExpiredModal(true)}
            disabled={loadingAction || displayStats.EXPIRED === 0}
          >
            🗑️ Wipe Expired
          </Button>
          <Button
            variant="outline-dark"
            size="sm"
            onClick={() => setShowWipeAllModal(true)}
            disabled={loadingAction || displayStats.TOTAL === 0}
          >
            🧨 Wipe All
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => fetchSessions(page, pageSize, statusFilter)}
            disabled={loading}
          >
            🔄 Refresh
          </Button>
        </div>
      </div>

      {/* ================= SEARCH & FILTER ================= */}
      <Row className="mb-2 g-3 align-items-center">
        <Col md={8} lg={9}>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <Select
              size="large"
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                setPage(0);
              }}
              options={SESSION_FILTER_OPTIONS}
              style={{
                minWidth: 180,
                height: 28
              }}
            />
          </div>
        </Col>

        <Col md={4} lg={3} className="text-end">
          <div style={{ fontSize: 13, color: "#64748b" }}>
            Results:{" "}
            <span className="fw-semibold text-dark">
              {sessions?.length || 0}
            </span>{" "}
            of{" "}
            <span className="fw-semibold text-dark">
              {totalElements}
            </span>
          </div>
        </Col>
      </Row>

      {/* ================= RESULTS & TABLE ================= */}
      {loading ? (
        <div style={{ minHeight: 200, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Spinner animation="border" variant="success" />
        </div>
      ) : sessions?.length === 0 ? (
        <div style={{ minHeight: 200, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Empty description="No guest sessions found" />
        </div>
      ) : (
        <div style={{ width: "100%", overflowX: "hidden" }}>
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              headerClassName="bg-light text-nowrap align-middle text-dark"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{ bordered: true, striped: true, className: "fs-10 mb-0" }}
            />
            <div className="mt-3">
              <TablePaginationFooter
                page={page}
                totalPages={totalPages}
                totalElements={totalElements}
                onPrev={() => setPage((p) => Math.max(p - 1, 0))}
                onNext={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
                pageSize={pageSize}
                onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
                pageSizeOptions={[10, 20, 30, 50, 100, 200, 500]}
              />
            </div>
          </AdvanceTableProvider>
        </div>
      )}

      {error && <div className="alert alert-danger mt-2">{error}</div>}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this guest session? This action cannot be undone.</p>
          {actionError && <div className="alert alert-warning mt-3">{actionError}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete} disabled={loadingAction}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={loadingAction}>
            {loadingAction ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Deleting...</> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Wipe Expired Confirmation Modal */}
      <Modal show={showWipeExpiredModal} onHide={() => setShowWipeExpiredModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Wipe Expired Sessions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete all expired guest sessions? This action cannot be undone.</p>
          <p className="text-muted">This will remove approximately {displayStats.EXPIRED} expired sessions.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowWipeExpiredModal(false)} disabled={loadingAction}>Cancel</Button>
          <Button variant="warning" onClick={handleWipeExpired} disabled={loadingAction}>
            {loadingAction ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Wiping...</> : "Wipe Expired"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Wipe All Confirmation Modal */}
      <Modal show={showWipeAllModal} onHide={() => setShowWipeAllModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Wipe All Sessions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete ALL guest sessions older than 30 days? This action cannot be undone.</p>
          <p className="text-muted">This will remove approximately {displayStats.TOTAL} sessions.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowWipeAllModal(false)} disabled={loadingAction}>Cancel</Button>
          <Button variant="danger" onClick={handleWipeAll} disabled={loadingAction}>
            {loadingAction ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Wiping...</> : "Wipe All"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Session Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Guest Session Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSession && (
            <div className="row">
              <div className="col-md-6 mb-3">
                <strong>Session ID:</strong>
                <p className="text-muted mb-0" style={{ fontSize: 12, wordBreak: "break-all" }}>
                  {selectedSession.sessionId}
                </p>
              </div>
              <div className="col-md-6 mb-3">
                <strong>User ID:</strong>
                <p className="text-muted mb-0" style={{ fontSize: 12, wordBreak: "break-all" }}>
                  {selectedSession.userId}
                </p>
              </div>
              <div className="col-md-6 mb-3">
                <strong>Device ID:</strong>
                <p className="text-muted mb-0" style={{ fontSize: 12, wordBreak: "break-all" }}>
                  {selectedSession.deviceId}
                </p>
              </div>
              <div className="col-md-6 mb-3">
                <strong>Platform:</strong>
                <p className="text-muted mb-0">{selectedSession.platform}</p>
              </div>
              <div className="col-md-6 mb-3">
                <strong>Client App:</strong>
                <p className="text-muted mb-0">{selectedSession.clientApp}</p>
              </div>
              <div className="col-md-6 mb-3">
                <strong>App Version:</strong>
                <p className="text-muted mb-0">{selectedSession.appVersion || "N/A"}</p>
              </div>
              <div className="col-md-6 mb-3">
                <strong>IP Address:</strong>
                <p className="text-muted mb-0">{selectedSession.ipAddress || "N/A"}</p>
              </div>
              <div className="col-md-6 mb-3">
                <strong>Request Count:</strong>
                <p className="text-muted mb-0">{selectedSession.requestCount || 0}</p>
              </div>
              <div className="col-md-6 mb-3">
                <strong>Status:</strong>
                <p className="text-muted mb-0">
                  <span className={`badge bg-${SESSION_STATUS_COLORS[selectedSession.status] || "secondary"}`}>
                    {selectedSession.status}
                  </span>
                </p>
              </div>
              <div className="col-md-6 mb-3">
                <strong>Created At:</strong>
                <p className="text-muted mb-0">
                  {selectedSession.createdAt ? new Date(selectedSession.createdAt).toLocaleString() : "N/A"}
                </p>
              </div>
              <div className="col-md-6 mb-3">
                <strong>Expires At:</strong>
                <p className="text-muted mb-0">
                  {selectedSession.expiresAt ? new Date(selectedSession.expiresAt).toLocaleString() : "N/A"}
                </p>
              </div>
              <div className="col-md-6 mb-3">
                <strong>Last Used:</strong>
                <p className="text-muted mb-0">
                  {selectedSession.lastUsedAt ? new Date(selectedSession.lastUsedAt).toLocaleString() : "N/A"}
                </p>
              </div>
              {selectedSession.revokedAt && (
                <div className="col-md-6 mb-3">
                  <strong>Revoked At:</strong>
                  <p className="text-muted mb-0">
                    {new Date(selectedSession.revokedAt).toLocaleString()}
                  </p>
                </div>
              )}
              {selectedSession.revokeReason && (
                <div className="col-md-6 mb-3">
                  <strong>Revoke Reason:</strong>
                  <p className="text-muted mb-0">{selectedSession.revokeReason}</p>
                </div>
              )}
              {selectedSession.userAgent && (
                <div className="col-12 mb-3">
                  <strong>User Agent:</strong>
                  <p className="text-muted mb-0" style={{ fontSize: 11, wordBreak: "break-all" }}>
                    {selectedSession.userAgent}
                  </p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default GuestSessions;